<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DemoEmbedModal from '@/components/common/DemoEmbedModal.vue';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import SectionTitle from '@/components/common/section-title.vue';
import { Marquee } from '@/components/inspira/marquee';
import { SafariMockup } from '@/components/inspira/safari-mockup';
import { AnimatedContent } from '@/components/vuebits';
import {
  PRODUCT_DEMO_URL,
  PRODUCT_GALLERY_CARDS,
  PRODUCT_MOCKUP_VIDEOS,
} from '@/config/mockup.js';

const ProductGalleryOverlay = defineAsyncComponent(() =>
  import('@/components/inspira/infinite-grid/ProductGalleryOverlay.vue'),
);

const { t } = useI18n();
const activeIndex = ref(0);
const galleryOpen = ref(false);
const demoOpen = ref(false);
const paused = ref(false);
const direction = ref(1);

/** 价值条跑马灯：离开视口 / 切走标签页时暂停 */
const valueStripRef = ref(null);
const productSectionRef = ref(null);
const stripInView = ref(false);
const productInView = ref(false);
const stripPageVisible = ref(typeof document === 'undefined' ? true : !document.hidden);
const stripPaused = computed(() => !stripInView.value || !stripPageVisible.value);
let stripObserver = null;
let productObserver = null;

const AUTOPLAY_MS = 5600;
let autoplayTimer = 0;

const valueItems = computed(() => [
  { label: t('valueStrip.1'), icon: 'hub' },
  { label: t('valueStrip.2'), icon: 'translate' },
  { label: t('valueStrip.3'), icon: 'edit_note' },
  { label: t('valueStrip.4'), icon: 'account_tree' },
  { label: t('valueStrip.5'), icon: 'groups' },
]);

/** 工作流四步 × 产品能力：同一叙事轴 */
const stages = computed(() => [
  {
    id: 'intake',
    index: '01',
    step: t('journey.step1Title'),
    capability: t('product.tab.draft'),
    title: t('product.panel.draftTitle'),
    desc: t('product.panel.draftDesc'),
    highlights: [t('journey.step1Highlight1'), t('journey.step1Highlight2')],
    previewUrl: 'app.clawbridge.io/inbox',
    mockupKey: 'draft',
  },
  {
    id: 'understand',
    index: '02',
    step: t('journey.step2Title'),
    capability: t('product.tab.translate'),
    title: t('product.panel.translateTitle'),
    desc: t('product.panel.translateDesc'),
    highlights: [t('journey.step2Highlight1'), t('journey.step2Highlight2')],
    previewUrl: 'app.clawbridge.io/translate',
    mockupKey: 'translate',
  },
  {
    id: 'followup',
    index: '03',
    step: t('journey.step3Title'),
    capability: t('product.tab.knowledge'),
    title: t('product.panel.knowledgeTitle'),
    desc: t('product.panel.knowledgeDesc'),
    highlights: [t('journey.step3Highlight1'), t('journey.step3Highlight2')],
    previewUrl: 'app.clawbridge.io/reply',
    mockupKey: 'knowledge',
  },
  {
    id: 'review',
    index: '04',
    step: t('journey.step4Title'),
    capability: t('product.tab.status'),
    title: t('product.panel.statusTitle'),
    desc: t('product.panel.statusDesc'),
    highlights: [t('journey.step4Highlight1'), t('journey.step4Highlight2')],
    previewUrl: 'app.clawbridge.io/pipeline',
    mockupKey: 'status',
  },
]);

const current = computed(() => stages.value[activeIndex.value] ?? stages.value[0]);

const stepLabel = computed(() => {
  const cur = String(activeIndex.value + 1).padStart(2, '0');
  const total = String(stages.value.length).padStart(2, '0');
  return `${cur} — ${total}`;
});

const metrics = computed(() => [
  { value: t('journey.metric1Value'), label: t('journey.metric1Label') },
  { value: t('journey.metric2Value'), label: t('journey.metric2Label') },
  { value: t('journey.metric3Value'), label: t('journey.metric3Label') },
]);

const productMockupVideo = computed(
  () => PRODUCT_MOCKUP_VIDEOS[current.value.mockupKey] ?? PRODUCT_MOCKUP_VIDEOS.draft,
);

/** 四步演示视频：进入视口后预加载，切换步骤走浏览器缓存 */
const mockupVideoSources = Object.values(PRODUCT_MOCKUP_VIDEOS);

/** 板块在视口内、未开弹层、标签页可见时才播视频 */
const mockupPlaying = computed(
  () => productInView.value && !galleryOpen.value && !demoOpen.value && stripPageVisible.value,
);

const anyOverlayOpen = computed(() => galleryOpen.value || demoOpen.value);

const galleryCards = computed(() => PRODUCT_GALLERY_CARDS.map((card, index) => ({
  ...card,
  title: t(`product.gallery.items.${index}.title`, card.title),
  description: t(`product.gallery.items.${index}.desc`, card.description),
  tags: card.tags.map((tag, tagIndex) => t(`product.gallery.items.${index}.tags.${tagIndex}`, tag)),
})));

function selectStage(index) {
  if (index === activeIndex.value) return;
  direction.value = index > activeIndex.value ? 1 : -1;
  activeIndex.value = index;
  restartAutoplay();
}

/** 手机直接跳转演示页；桌面打开 iframe 弹窗 */
function openDemo() {
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
    window.open(PRODUCT_DEMO_URL, '_blank', 'noopener,noreferrer');
    return;
  }
  demoOpen.value = true;
}

function scrollActiveStageTab() {
  if (typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches) return;
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-stage-tab="${activeIndex.value}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  });
}

watch(activeIndex, () => {
  scrollActiveStageTab();
});

function prevStage() {
  if (activeIndex.value <= 0) return;
  direction.value = -1;
  activeIndex.value -= 1;
  restartAutoplay();
}

function nextStage() {
  if (activeIndex.value >= stages.value.length - 1) {
    direction.value = 1;
    activeIndex.value = 0;
  } else {
    direction.value = 1;
    activeIndex.value += 1;
  }
  restartAutoplay();
}

function clearAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = 0;
  }
}

function startAutoplay() {
  clearAutoplay();
  if (paused.value || !productInView.value || anyOverlayOpen.value || document.hidden) return;
  autoplayTimer = window.setInterval(() => {
    direction.value = 1;
    activeIndex.value = (activeIndex.value + 1) % stages.value.length;
  }, AUTOPLAY_MS);
}

function restartAutoplay() {
  startAutoplay();
}

function onStageEnter() {
  paused.value = true;
  clearAutoplay();
}

function onStageLeave() {
  paused.value = false;
  startAutoplay();
}

watch(anyOverlayOpen, (open) => {
  if (open) {
    clearAutoplay();
    return;
  }
  if (!paused.value) startAutoplay();
});

watch(productInView, (inView) => {
  if (inView) {
    if (!paused.value && !anyOverlayOpen.value) startAutoplay();
    return;
  }
  clearAutoplay();
});

function onPageVisibilityChange() {
  stripPageVisible.value = !document.hidden;
  if (document.hidden) {
    clearAutoplay();
    return;
  }
  if (productInView.value && !paused.value && !anyOverlayOpen.value) startAutoplay();
}

onMounted(() => {
  stripObserver = new IntersectionObserver(
    ([entry]) => {
      stripInView.value = entry?.isIntersecting ?? false;
    },
    { threshold: 0.05 },
  );
  if (valueStripRef.value) stripObserver.observe(valueStripRef.value);

  productObserver = new IntersectionObserver(
    ([entry]) => {
      productInView.value = entry?.isIntersecting ?? false;
    },
    { threshold: 0.15 },
  );
  if (productSectionRef.value) productObserver.observe(productSectionRef.value);

  document.addEventListener('visibilitychange', onPageVisibilityChange);
});

onBeforeUnmount(() => {
  clearAutoplay();
  stripObserver?.disconnect();
  stripObserver = null;
  productObserver?.disconnect();
  productObserver = null;
  document.removeEventListener('visibilitychange', onPageVisibilityChange);
});
</script>

<template>
  <!-- 价值条：跑马灯，离开视口暂停 -->
  <section
    ref="valueStripRef"
    class="relative overflow-hidden border-b border-slate-200/80 py-5"
    aria-label="value strip"
  >
    <Marquee
      :paused="stripPaused"
      pause-on-hover
      :repeat="3"
      class="[--duration:32s] [--gap:3rem] p-0"
    >
      <div
        v-for="item in valueItems"
        :key="item.label"
        class="flex shrink-0 items-center gap-2.5 text-sm font-medium text-slate-600"
      >
        <MaterialIcon :name="item.icon" class="text-lg text-primary" />
        {{ item.label }}
      </div>
    </Marquee>
  </section>

  <!-- 产品能力 × 工作流程：开放排版 -->
  <section
    id="product"
    ref="productSectionRef"
    class="relative overflow-hidden pb-[clamp(4rem,10vw,8rem)] pt-14 md:pt-20"
  >
    <div id="journey" class="pointer-events-none absolute top-0 h-px w-px" aria-hidden="true" />

    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ t('product.eyebrow') }}</p>
      </AnimatedContent>
      <SectionTitle :title="t('product.title')" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ t('product.subtitle') }}
        </p>
      </AnimatedContent>

      <div
        class="mt-10 md:mt-14"
        @mouseenter="onStageEnter"
        @mouseleave="onStageLeave"
      >
        <!-- 步骤导航：手机横滑，平板+ 四列底线 -->
        <nav :aria-label="t('journey.workflow')">
          <ul
            class="stage-nav flex gap-1 overflow-x-auto border-b border-slate-200/80 pb-px [-ms-overflow-style:none] [scrollbar-width:none] md:grid md:grid-cols-4 md:gap-6 md:overflow-visible"
          >
            <li
              v-for="(stage, index) in stages"
              :key="stage.id"
              class="min-w-0 shrink-0 md:shrink"
            >
              <button
                type="button"
                class="group relative w-auto min-w-[7.25rem] px-1 pb-3.5 text-left transition-colors md:w-full md:min-w-0 md:px-0 md:pb-4"
                :data-stage-tab="index"
                :aria-current="activeIndex === index ? 'step' : undefined"
                @click="selectStage(index)"
              >
                <span
                  class="block text-[12px] font-medium tabular-nums tracking-wide transition-colors md:text-[13px]"
                  :class="activeIndex === index ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'"
                >
                  {{ stage.index }}
                </span>
                <span
                  class="mt-1 block whitespace-nowrap text-[13px] font-semibold tracking-tight transition-colors md:mt-1.5 md:truncate md:whitespace-normal md:text-base"
                  :class="activeIndex === index ? 'text-foreground' : 'text-slate-400 group-hover:text-slate-700'"
                >
                  {{ stage.step }}
                </span>
                <span
                  class="absolute inset-x-1 -bottom-px h-0.5 origin-left bg-primary transition-transform duration-300 ease-out md:inset-x-0"
                  :class="activeIndex === index ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50 group-hover:bg-slate-300'"
                />
              </button>
            </li>
          </ul>
        </nav>

        <!--
          主内容：仅手机用 contents + order 重排（文案 → 预览 → 要点 → 操作 → 指标）
          md+ 恢复原 DOM 顺序；lg 为左文右图 12 栏
        -->
        <div
          class="mt-8 flex flex-col gap-7 md:mt-14 md:grid md:gap-10 lg:grid-cols-12 lg:items-start lg:gap-16"
        >
          <div class="contents md:block lg:col-span-4">
            <Transition
              mode="out-in"
              enter-active-class="transition duration-300 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div :key="current.id" class="order-1 md:order-none">
                <p class="text-sm font-medium text-primary">
                  {{ current.capability }}
                </p>

                <h3 class="mt-3 text-2xl font-bold leading-tight tracking-tight text-foreground md:mt-4 md:text-3xl">
                  {{ current.title }}
                </h3>

                <p class="mt-3 text-[15px] leading-7 text-muted-foreground md:mt-5 md:text-base">
                  {{ current.desc }}
                </p>

                <ul class="mt-9 hidden space-y-3.5 md:block">
                  <li
                    v-for="(highlight, hi) in current.highlights"
                    :key="hi"
                    class="flex gap-3 text-[15px] leading-6 text-slate-600"
                  >
                    <span class="mt-[0.7rem] h-px w-3 shrink-0 bg-primary/70" />
                    {{ highlight }}
                  </li>
                </ul>
              </div>
            </Transition>

            <!-- 手机要点：预览下方 -->
            <Transition
              mode="out-in"
              enter-active-class="transition duration-300 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ul :key="`hl-${current.id}`" class="order-3 space-y-3 md:hidden">
                <li
                  v-for="(highlight, hi) in current.highlights"
                  :key="hi"
                  class="flex gap-3 text-sm leading-6 text-slate-600"
                >
                  <span class="mt-[0.65rem] h-px w-3 shrink-0 bg-primary/70" />
                  {{ highlight }}
                </li>
              </ul>
            </Transition>

            <div class="order-4 mt-0 flex items-center gap-5 md:order-none md:mt-12">
              <button
                type="button"
                class="flex items-center text-slate-400 transition hover:text-foreground disabled:opacity-25"
                :disabled="activeIndex === 0"
                :aria-label="t('journey.prevStep')"
                @click="prevStage"
              >
                <MaterialIcon name="arrow_back" class="text-xl" />
              </button>
              <span class="text-sm tabular-nums tracking-wide text-slate-400">
                {{ stepLabel }}
              </span>
              <button
                type="button"
                class="flex items-center text-slate-700 transition hover:text-primary"
                :aria-label="t('journey.nextStep')"
                @click="nextStage"
              >
                <MaterialIcon name="arrow_forward" class="text-xl" />
              </button>
            </div>

            <div class="order-5 mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 md:order-none md:mt-8">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-[gap] hover:gap-2.5"
                @click="openDemo"
              >
                {{ t('product.tryDemo') }}
                <MaterialIcon name="play_circle" class="text-base" />
              </button>
              <!-- <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-[gap] hover:gap-2.5 hover:text-foreground"
                @click="galleryOpen = true"
              >
                {{ t('product.viewMore') }}
                <MaterialIcon name="arrow_forward" class="text-base" />
              </button> -->
            </div>
          </div>

          <div class="order-2 min-w-0 overflow-hidden md:order-none lg:col-span-8">
            <!-- 单实例切换 video-src，避免 remount + 淡出淡入闪烁 -->
            <SafariMockup
              :url="current.previewUrl"
              :video-src="productMockupVideo"
              :video-sources="mockupVideoSources"
              :playing="mockupPlaying"
              :content-aspect-ratio="16 / 8.2"
              object-fit="cover"
              :typewriter="false"
              :radius="14"
              video-preload="auto"
              class="w-full"
            />

            <dl class="mt-8 hidden flex-wrap items-baseline gap-x-10 gap-y-2 md:flex">
              <div
                v-for="metric in metrics"
                :key="metric.label"
                class="flex items-baseline gap-2"
              >
                <dt class="sr-only">{{ metric.label }}</dt>
                <dd class="text-xl font-bold tracking-tight text-foreground tabular-nums">
                  {{ metric.value }}
                </dd>
                <span class="text-sm text-muted-foreground">{{ metric.label }}</span>
              </div>
            </dl>
          </div>

          <!-- 手机指标 -->
          <dl class="order-6 flex flex-col gap-3 border-t border-slate-200/80 pt-5 md:hidden">
            <div
              v-for="metric in metrics"
              :key="metric.label"
              class="flex items-baseline justify-between gap-3"
            >
              <dt class="text-sm text-muted-foreground">{{ metric.label }}</dt>
              <dd class="text-lg font-bold tracking-tight text-foreground tabular-nums">
                {{ metric.value }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <ProductGalleryOverlay
      :open="galleryOpen"
      :card-data="galleryCards"
      @close="galleryOpen = false"
    />

    <DemoEmbedModal
      v-model="demoOpen"
      :src="PRODUCT_DEMO_URL"
      :display-url="PRODUCT_DEMO_URL.replace(/^https?:\/\//, '')"
    />
  </section>
</template>

<style scoped>
.stage-nav::-webkit-scrollbar {
  display: none;
}
</style>
